import type { MappingEntryDto, PreviewBody } from "./types";

export const PREVIEW_DEBOUNCE_MS = 250;

/** POST .../preview. `overrides` carries the unsaved rows so a binding previews before it is written. */
export interface PreviewRequest {
  sample?: unknown;
  overrides?: Record<string, MappingEntryDto | null>;
}

export type PreviewSend = (body: PreviewRequest, signal: AbortSignal) => Promise<PreviewBody>;

export interface PreviewHandlers {
  onResult(body: PreviewBody): void;
  onError?(error: unknown): void;
}

export interface PreviewScheduler {
  schedule(body: PreviewRequest): void;
  cancel(): void;
}

export function createPreviewScheduler(
  send: PreviewSend,
  handlers: PreviewHandlers,
  delay = PREVIEW_DEBOUNCE_MS,
): PreviewScheduler {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let inFlight: AbortController | undefined;

  const cancel = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    inFlight?.abort();
    inFlight = undefined;
  };

  return {
    schedule(body) {
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = undefined;
        // A newer run supersedes the one still on the wire.
        inFlight?.abort();
        const current = new AbortController();
        inFlight = current;
        send(body, current.signal).then(
          (result) => {
            if (!current.signal.aborted) handlers.onResult(result);
          },
          (error: unknown) => {
            if (!current.signal.aborted) handlers.onError?.(error);
          },
        );
      }, delay);
    },
    cancel,
  };
}
